class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isDiscreetSwapInProgress = false;
    this.isTreatUpsellAddInProgress = false;
    this.dismissedTreatUpsellKeysForCurrentOpen = new Set();
    this.dismissedPdpTreatUpsellKeys = new Set();
    this.didCleanLegacyDiscreetDismissal = false;
    this.treatUpsellTimers = new Map();
    this.treatUpsellTimerInterval = null;

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
    this.initPdpTreatUpsells();
  }

  setHeaderCartIconAccessibility() {
    document
      .querySelectorAll('[id*="cart-icon-bubble"]')
      .forEach((cartLink) => {
        cartLink.setAttribute("role", "button");
        cartLink.setAttribute("aria-haspopup", "dialog");
        cartLink.addEventListener("click", (event) => {
          event.preventDefault();
          this.open(cartLink);
        });
        cartLink.addEventListener("keydown", (event) => {
          if (event.code.toUpperCase() === "SPACE") {
            event.preventDefault();
            this.open(cartLink);
          }
        });
      });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (triggeredBy || !this.classList.contains("active")) {
      this.dismissedTreatUpsellKeysForCurrentOpen = new Set();
    }

    const warrantySection = this.querySelector("warranty-section");
    const cartItems = this.querySelector("cart-drawer-items");
    const footer = this.querySelector(".drawer__footer-footer");
    const header = this.querySelector(".drawer__footer-content");

    // Check if excluded product is in cart
    const excludedHandles = [
      "hydropet-stainless-steel-dog-water-bottle",
      "insect-shield-dog-bandana",
      "trailblazer-outdoor-pack",
      "dog-silencer-max-preowned",
      "open-box-solar-pack-max",
    ];
    const cartItemNodes = this.querySelectorAll(".drawer__cart-item");
    let hasExcludedProduct = false;
    cartItemNodes.forEach((itemNode) => {
      const itemHandle = String(itemNode.dataset.itemHandle || "").trim().toLowerCase();
      if (excludedHandles.includes(itemHandle)) {
        hasExcludedProduct = true;
      }
    });

    if (warrantySection) {
      if (triggeredBy || this.skipWarranty || hasExcludedProduct) {
        warrantySection.setAttribute("hidden", "");
        if (cartItems) cartItems.removeAttribute("hidden");
        if (footer) footer.removeAttribute("hidden");
        if (header) header.removeAttribute("hidden");
        this.skipWarranty = false;
      } else {
        warrantySection.removeAttribute("hidden");
        if (cartItems) cartItems.setAttribute("hidden", "");
        if (footer) footer.setAttribute("hidden", "");
        if (header) header.setAttribute("hidden", "");
        this.updateWarrantyPrices();
        this.initWarrantyListeners();
      }
    }

    this.refreshCartUpsells();

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.style.paddingRight = this.getScrollbarWidth() + "px";
    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    this.dismissedTreatUpsellKeysForCurrentOpen = new Set();
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
    document.body.style.paddingRight = "";
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;

    if (parsedState.price) {
      this.lastAddedItemPrice = parsedState.price;
      this.lastAddedItemSku = parsedState.sku;
    }

    // Sync the cart-drawer container's is-empty class from the refreshed section HTML.
    // Without this the initial empty-cart class persists, which hides .drawer__footer
    // via CSS and leaves the drawer visually blank after adding the first item.
    const cartDrawerSectionHtml =
      parsedState.sections && parsedState.sections["cart-drawer"];
    if (cartDrawerSectionHtml) {
      const freshCartDrawer = new DOMParser()
        .parseFromString(cartDrawerSectionHtml, "text/html")
        .querySelector("cart-drawer");
      if (freshCartDrawer) {
        this.classList.toggle(
          "is-empty",
          freshCartDrawer.classList.contains("is-empty")
        );
      }
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (sectionElement) {
        const innerHtml = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
        if (innerHtml !== null) {
          sectionElement.innerHTML = innerHtml;
        }
      }
      if (
        section.id == "cart-free-delivery" &&
        document.querySelector("#product-page-free-delivery")
      ) {
        const freeDeliveryHtml = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
        if (freeDeliveryHtml !== null) {
          document.querySelector("#product-page-free-delivery").innerHTML =
            freeDeliveryHtml;
        }
      }
    });

    this.initPdpTreatUpsells();
    document.dispatchEvent(new CustomEvent("cart:refresh"));

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    if (!html) return null;
    const element = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
    if (!element) {
      console.warn(
        `[CartDrawer] Could not find "${selector}" in fetched section HTML.`
      );
      return null;
    }
    return element.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
      {
        id: "cart-icon-bubble--mobile",
      },
      {
        id: "cart-free-delivery",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  refreshCartUpsells() {
    this.initDiscreetNeighborUpsell();
    this.initTreatUpsells();
  }

  initDiscreetNeighborUpsell() {
    const upsell = this.querySelector("[data-discreet-neighbor-upsell]");
    if (!upsell) return;
    const config = this.getDiscreetUpsellConfig(upsell);
    this.clearLegacyDiscreetUpsellDismissed();

    const acceptBtn = upsell.querySelector("[data-dnp-upsell-accept]");
    const dismissBtn = upsell.querySelector("[data-dnp-upsell-dismiss]");
    const variantSelect = upsell.querySelector("[data-dnp-upsell-variant-select]");

    if (acceptBtn && upsell.dataset.bound !== "true") {
      acceptBtn.addEventListener("click", (event) => {
        event.preventDefault();
        this.swapDogSilencerToDiscreetPack(upsell);
      });
    }

    if (dismissBtn && upsell.dataset.bound !== "true") {
      dismissBtn.addEventListener("click", (event) => {
        event.preventDefault();
        this.setDiscreetUpsellDismissed(config);
        this.hideDiscreetUpsell(upsell);
      });
    }

    if (variantSelect && upsell.dataset.bound !== "true") {
      variantSelect.addEventListener("change", (event) => {
        event.stopPropagation();
        this.syncDiscreetUpsellSelectedVariant(upsell);
      });
    }

    this.syncDiscreetUpsellSelectedVariant(upsell);
    upsell.dataset.bound = "true";
    this.evaluateDiscreetNeighborEligibility();
  }

  getDiscreetUpsellConfig(upsell) {
    const sourceHandle = String(upsell.dataset.sourceHandle || "").trim();
    const targetProductId = Number(upsell.dataset.targetProductId);
    const targetHandle = String(upsell.dataset.targetHandle || "").trim();
    const discountCode = String(upsell.dataset.discountCode || "").trim();
    const selectedVariantId = Number(
      upsell.querySelector("[data-dnp-upsell-variant-select]")?.value
    );

    return {
      sourceHandle,
      targetProductId,
      targetHandle,
      discountCode,
      selectedVariantId,
      sourceVariantMap: {
        [Number(upsell.dataset.sourceVariantNoFaceplate)]: Number(
          upsell.dataset.variantNoFaceplate
        ),
        [Number(upsell.dataset.sourceVariantMatteWhite)]: Number(
          upsell.dataset.variantMatteWhite
        ),
        [Number(upsell.dataset.sourceVariantCamouflagePattern)]: Number(
          upsell.dataset.variantCamouflagePattern
        ),
        [Number(upsell.dataset.sourceVariantTreeBark)]: Number(
          upsell.dataset.variantTreeBark
        ),
      },
      variantMap: {
        "no faceplate": Number(upsell.dataset.variantNoFaceplate),
        "matte white": Number(upsell.dataset.variantMatteWhite),
        "camouflage pattern": Number(upsell.dataset.variantCamouflagePattern),
        "tree bark": Number(upsell.dataset.variantTreeBark),
      },
    };
  }

  syncDiscreetUpsellSelectedVariant(upsell) {
    const variantSelect = upsell?.querySelector("[data-dnp-upsell-variant-select]");
    const priceElement = upsell?.querySelector("[data-dnp-upsell-price]");
    if (!variantSelect || !priceElement) return;

    const selectedOption = variantSelect.options[variantSelect.selectedIndex];
    const priceCents = Number(selectedOption?.dataset.price);
    if (Number.isFinite(priceCents) && priceCents > 0) {
      priceElement.textContent = this.formatMoney(priceCents);
    }
  }

  getCartTreatUpsells() {
    return Array.from(
      this.querySelectorAll('[data-treat-upsell][data-upsell-surface="cart"]')
    );
  }

  getPdpTreatUpsells() {
    return Array.from(
      document.querySelectorAll('[data-treat-upsell][data-upsell-surface="pdp"]')
    );
  }

  initTreatUpsells() {
    const upsells = this.getCartTreatUpsells();
    if (!upsells.length) return;

    upsells.forEach((upsell) => {
      this.bindTreatUpsellEvents(upsell);
      this.syncTreatUpsellVariant(upsell);
    });

    this.evaluateTreatUpsellEligibility();
  }

  initPdpTreatUpsells() {
    const upsells = this.getPdpTreatUpsells();
    if (!upsells.length) return;

    upsells.forEach((upsell) => {
      this.bindTreatUpsellEvents(upsell);
      this.syncTreatUpsellVariant(upsell);
    });

    this.bindPdpVariantChangeListener();
    this.evaluatePdpTreatUpsellEligibility();
  }

  bindPdpVariantChangeListener() {
    if (this.pdpVariantListenerBound) return;
    this.pdpVariantListenerBound = true;
    document.addEventListener("pdp:variant:changed", () => {
      this.evaluatePdpTreatUpsellEligibility();
    });
  }

  bindTreatUpsellEvents(upsell) {
    if (!upsell || upsell.dataset.bound === "true") return;

    const acceptBtn = upsell.querySelector("[data-ot-upsell-accept]");
    const dismissBtn = upsell.querySelector("[data-ot-upsell-dismiss]");
    const variantSelect = upsell.querySelector("[data-ot-upsell-variant-select]");

    if (acceptBtn) {
      acceptBtn.addEventListener("click", (event) => {
        event.preventDefault();
        this.addTreatUpsellItem(upsell);
      });
    }

    if (dismissBtn) {
      dismissBtn.addEventListener("click", (event) => {
        event.preventDefault();
        this.dismissTreatUpsell(upsell);
      });
    }

    if (variantSelect) {
      variantSelect.addEventListener("change", () => {
        this.syncTreatUpsellVariant(upsell);
      });
    }

    upsell.dataset.bound = "true";
  }

  getTreatUpsellConfig(upsell) {
    const sourceHandles = String(upsell?.dataset.sourceHandles || "")
      .split(",")
      .map((handle) => String(handle || "").trim().toLowerCase())
      .filter(Boolean);
    const hiddenIfHandles = String(upsell?.dataset.hideIfHandles || "")
      .split(",")
      .map((handle) => String(handle || "").trim().toLowerCase())
      .filter(Boolean);
    const targetProductId = Number(upsell?.dataset.targetProductId);
    const targetVariantId = Number(upsell?.dataset.targetVariantId);
    const offerKey = String(upsell?.dataset.offerKey || "").trim().toLowerCase();
    const surface = String(upsell?.dataset.upsellSurface || "").trim().toLowerCase();
    const savingsCents = Number(upsell?.dataset.savingsCents);
    const savingsLabel = String(upsell?.dataset.savingsLabel || "").trim();
    const targetLabel = String(upsell?.dataset.targetLabel || "treats").trim();
    const urgencyDurationSeconds = Number(upsell?.dataset.urgencyDurationSeconds);
    const sourceProductId = Number(upsell?.dataset.sourceProductId);
    const sourceHandle = String(upsell?.dataset.sourceProductHandle || "")
      .trim()
      .toLowerCase();

    return {
      sourceHandles,
      hiddenIfHandles,
      targetProductId,
      targetVariantId,
      offerKey,
      surface,
      savingsCents,
      savingsLabel,
      targetLabel,
      urgencyDurationSeconds,
      sourceProductId,
      sourceHandle,
    };
  }

  getPdpSelectedSourceVariantId(config) {
    const sourceProductId = Number(config?.sourceProductId);
    const candidates = [];

    // 1. [name="id"] inside the main product form. Shopify's VariantSelects
    //    updates this input's value on every variant change, even though it
    //    stays `disabled` in the Liquid template.
    const productFormInput = document.querySelector(
      'product-form form[action*="/cart/add"] [name="id"]'
    );
    if (productFormInput) candidates.push(Number(productFormInput.value));

    // 2. <select name="id"> inside <variant-selects>, linked to the form via
    //    the form= attribute (lives outside <product-form>).
    const variantSelect = document.querySelector(
      'variant-selects select[name="id"], variant-radios select[name="id"]'
    );
    if (variantSelect) candidates.push(Number(variantSelect.value));

    // 3. ?variant= URL param (set by Shopify after variant change).
    try {
      const variantParam = new URLSearchParams(window.location.search).get(
        "variant"
      );
      if (variantParam) candidates.push(Number(variantParam));
    } catch (error) {
      // URLSearchParams unavailable; ignore.
    }

    // 4. <variant-selects>.currentVariant (set by the custom element after hydration).
    const variantHost =
      document.querySelector("variant-selects") ||
      document.querySelector("variant-radios");
    const currentVariantId = Number(variantHost?.currentVariant?.id);
    if (Number.isFinite(currentVariantId)) candidates.push(currentVariantId);

    // Pick the first valid candidate that belongs to the expected source product
    // when we can verify via <variant-selects>' variant JSON; otherwise the first
    // valid candidate.
    const variantJsonScript = variantHost?.querySelector(
      'script[type="application/json"]'
    );
    let variantLookup = null;
    if (variantJsonScript) {
      try {
        const parsed = JSON.parse(variantJsonScript.textContent || "[]");
        if (Array.isArray(parsed)) {
          variantLookup = new Map(
            parsed.map((v) => [Number(v.id), v])
          );
        }
      } catch (error) {
        variantLookup = null;
      }
    }

    for (const id of candidates) {
      if (!Number.isFinite(id) || id <= 0) continue;
      if (
        variantLookup &&
        Number.isFinite(sourceProductId) &&
        sourceProductId > 0
      ) {
        // Only trust a candidate if it maps to a variant of the expected source
        // product. Prevents an unrelated product-form on the page from leaking in.
        const match = variantLookup.get(id);
        if (!match) continue;
      }
      return id;
    }

    return null;
  }

  isPdpSourceVariantAvailable(upsell, config) {
    const variantHost =
      document.querySelector("variant-selects") ||
      document.querySelector("variant-radios");
    const variantJsonScript = variantHost?.querySelector(
      'script[type="application/json"]'
    );

    const selectedId = this.getPdpSelectedSourceVariantId(config);

    if (variantJsonScript) {
      try {
        const parsed = JSON.parse(variantJsonScript.textContent || "[]");
        if (Array.isArray(parsed)) {
          const lookup = new Map(parsed.map((v) => [Number(v.id), v]));
          if (Number.isFinite(selectedId) && lookup.has(selectedId)) {
            return lookup.get(selectedId).available === true;
          }
          // Fall back to the live currentVariant if available.
          const currentId = Number(variantHost?.currentVariant?.id);
          if (Number.isFinite(currentId) && lookup.has(currentId)) {
            return lookup.get(currentId).available === true;
          }
        }
      } catch (error) {
        // Ignore parse errors and fall back to the Liquid-provided value.
      }
    }

    // Initial load / no variant data: trust the value rendered by Liquid.
    return upsell?.dataset.sourceVariantAvailable !== "false";
  }

  getPdpSelectedSourceQuantity() {
    const productForm = document.querySelector(
      'product-form form[action*="/cart/add"]'
    );
    if (!productForm) return 1;
    const qtyInput = productForm.querySelector('[name="quantity"]');
    const qty = Number(qtyInput?.value);
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  updateTreatUpsellCtaCopy(upsell, hasSource) {
    if (!upsell) return;
    const addBoth = upsell.querySelector("[data-cta-add-both]");
    const treatsOnly = upsell.querySelector("[data-cta-treats-only]");
    if (!addBoth || !treatsOnly) return;
    addBoth.hidden = hasSource;
    treatsOnly.hidden = !hasSource;
  }

  getSelectedTreatVariantId(upsell) {
    const variantSelect = upsell?.querySelector("[data-ot-upsell-variant-select]");
    if (!variantSelect) return null;

    const variantId = Number(variantSelect.value);
    return Number.isFinite(variantId) ? variantId : null;
  }

  syncTreatUpsellVariant(upsell) {
    const config = this.getTreatUpsellConfig(upsell);
    const variantSelect = upsell?.querySelector("[data-ot-upsell-variant-select]");
    const comparePriceElement = upsell?.querySelector("[data-ot-upsell-compare-price]");
    const priceElement = upsell?.querySelector("[data-ot-upsell-price]");
    const savingsElement = upsell?.querySelector("[data-ot-upsell-savings]");
    if (!variantSelect || !priceElement) return;

    const selectedOption = variantSelect.options[variantSelect.selectedIndex];
    const comparePriceCents = Number(selectedOption?.dataset.comparePrice);
    if (!Number.isFinite(comparePriceCents)) return;

    const bundlePriceCents = Number(selectedOption?.dataset.bundlePrice);
    const calculatedBundlePriceCents = Number.isFinite(config.savingsCents) && config.savingsCents > 0
      ? comparePriceCents - config.savingsCents
      : NaN;
    const discountedPriceCents =
      Number.isFinite(calculatedBundlePriceCents) && calculatedBundlePriceCents >= 0
        ? calculatedBundlePriceCents
        : Number.isFinite(bundlePriceCents) && bundlePriceCents > 0
        ? bundlePriceCents
        : comparePriceCents;

    if (comparePriceElement) {
      comparePriceElement.textContent = this.formatMoney(comparePriceCents);
    }

    priceElement.textContent = this.formatMoney(discountedPriceCents);

    if (savingsElement) {
      if (config.savingsLabel) {
        savingsElement.textContent = config.savingsLabel;
      } else if (Number.isFinite(config.savingsCents) && config.savingsCents > 0) {
        savingsElement.textContent = this.formatSavingsLabel(config.savingsCents);
      }
    }
  }

  async evaluateDiscreetNeighborEligibility(cartState = null) {
    const upsell = this.querySelector("[data-discreet-neighbor-upsell]");
    if (!upsell) return false;

    const config = this.getDiscreetUpsellConfig(upsell);
    if (!config.sourceHandle || !Number.isFinite(config.targetProductId)) {
      this.hideDiscreetUpsell(upsell);
      return false;
    }

    try {
      const drawerState = this.getDrawerEligibilityState(config);
      const hasSource = drawerState.hasSource;
      const hasTarget = drawerState.hasTarget;

      // If DSM is gone from cart, reset this offer's dismissal so a new add can show again.
      if (!hasSource) {
        this.clearDiscreetUpsellDismissed(config);
        this.hideDiscreetUpsell(upsell);
        return false;
      }

      if (this.isDiscreetUpsellDismissed(config)) {
        this.hideDiscreetUpsell(upsell);
        return false;
      }

      const shouldShow = !hasTarget;
      upsell.hidden = !shouldShow;
      return shouldShow;
    } catch (error) {
      console.error("[DNP Upsell] Eligibility check failed", error);
      this.hideDiscreetUpsell(upsell);
      return false;
    }
  }

  async evaluateTreatUpsellEligibility() {
    const upsells = this.getCartTreatUpsells();
    if (!upsells.length) return false;

    try {
      let hasVisibleUpsell = false;

      upsells.forEach((upsell) => {
        const config = this.getTreatUpsellConfig(upsell);
        if (
          !config.sourceHandles.length ||
          !Number.isFinite(config.targetProductId) ||
          !Number.isFinite(config.targetVariantId)
        ) {
          this.hideTreatUpsell(upsell);
          return;
        }

        const drawerState = this.getDrawerTreatUpsellState(config);
        const isDismissed =
          config.offerKey &&
          this.dismissedTreatUpsellKeysForCurrentOpen.has(config.offerKey);
        const isExpired = this.isTreatUpsellTimerExpired(config);
        const shouldShow =
          drawerState.hasSource &&
          !drawerState.hasTarget &&
          !drawerState.hasHiddenHandle &&
          !isDismissed &&
          !isExpired;

        upsell.hidden = !shouldShow;
        if (shouldShow) {
          hasVisibleUpsell = true;
          this.ensureTreatUpsellTimerStarted(upsell, config);
        }
        this.renderTreatUpsellUrgency(upsell, config);
      });

      return hasVisibleUpsell;
    } catch (error) {
      console.error("[Treat Upsell] Cart eligibility check failed", error);
      this.hideAllTreatUpsells(this.getCartTreatUpsells());
      return false;
    }
  }

  async evaluatePdpTreatUpsellEligibility(cartState = null) {
    const upsells = this.getPdpTreatUpsells();
    if (!upsells.length) return false;

    try {
      const cart = cartState || (await this.fetchCartJson());
      let hasVisibleUpsell = false;

      upsells.forEach((upsell) => {
        const config = this.getTreatUpsellConfig(upsell);
        if (!Number.isFinite(config.targetProductId) || !Number.isFinite(config.targetVariantId)) {
          this.hideTreatUpsell(upsell);
          return;
        }

        const cartStateForUpsell = this.getCartTreatUpsellState(cart, config);
        const isDismissed =
          config.offerKey &&
          this.dismissedPdpTreatUpsellKeys.has(config.offerKey);
        const isSourceVariantAvailable = this.isPdpSourceVariantAvailable(
          upsell,
          config
        );
        const shouldShow =
          isSourceVariantAvailable &&
          !cartStateForUpsell.hasTarget &&
          !cartStateForUpsell.hasHiddenHandle &&
          !isDismissed;

        upsell.hidden = !shouldShow;
        this.updateTreatUpsellCtaCopy(upsell, cartStateForUpsell.hasSource);
        if (shouldShow) hasVisibleUpsell = true;
      });

      return hasVisibleUpsell;
    } catch (error) {
      console.error("[Treat Upsell] PDP eligibility check failed", error);
      this.hideAllTreatUpsells(this.getPdpTreatUpsells());
      return false;
    }
  }

  getDrawerEligibilityState(config) {
    const cartItemNodes = this.querySelectorAll(
      "#CartDrawer-CartItems .drawer__cart-item"
    );

    let hasSource = false;
    let hasTarget = false;

    cartItemNodes.forEach((itemNode) => {
      const isWarranty = itemNode.dataset.isWarranty === "true";
      if (isWarranty) return;

      const skipUpsell = itemNode.dataset.upsellSkip === "true";
      const itemHandle = String(itemNode.dataset.itemHandle || "")
        .trim()
        .toLowerCase();
      if (
        !skipUpsell &&
        itemHandle &&
        itemHandle === String(config.sourceHandle).toLowerCase()
      ) {
        hasSource = true;
      }

      const productId = Number(itemNode.dataset.productId);
      if (Number.isFinite(productId) && productId === Number(config.targetProductId)) {
        hasTarget = true;
      }
    });

    return {
      hasSource,
      hasTarget,
    };
  }

  getDrawerTreatUpsellState(config) {
    const cartItemNodes = this.querySelectorAll(
      "#CartDrawer-CartItems .drawer__cart-item"
    );

    let hasSource = false;
    let hasTarget = false;
    let hasHiddenHandle = false;

    cartItemNodes.forEach((itemNode) => {
      const isWarranty = itemNode.dataset.isWarranty === "true";
      if (isWarranty) return;

      const skipUpsell = itemNode.dataset.upsellSkip === "true";
      const itemHandle = String(itemNode.dataset.itemHandle || "")
        .trim()
        .toLowerCase();

      if (!skipUpsell && itemHandle && config.sourceHandles.includes(itemHandle)) {
        hasSource = true;
      }

      if (!skipUpsell && itemHandle && config.hiddenIfHandles.includes(itemHandle)) {
        hasHiddenHandle = true;
      }

      const productId = Number(itemNode.dataset.productId);
      if (Number.isFinite(productId) && productId === Number(config.targetProductId)) {
        hasTarget = true;
      }
    });

    return {
      hasSource,
      hasTarget,
      hasHiddenHandle,
    };
  }

  getDiscreetUpsellDismissStorageKey(config) {
    const source = String(config?.sourceHandle || "")
      .trim()
      .toLowerCase();
    const targetProductId = Number(config?.targetProductId);
    if (!source || !Number.isFinite(targetProductId)) return null;
    return `dnp_upsell_dismissed:${source}:${targetProductId}`;
  }

  isDiscreetUpsellDismissed(config) {
    const storageKey = this.getDiscreetUpsellDismissStorageKey(config);
    if (!storageKey) return false;

    try {
      return sessionStorage.getItem(storageKey) === "1";
    } catch (error) {
      return false;
    }
  }

  setDiscreetUpsellDismissed(config) {
    const storageKey = this.getDiscreetUpsellDismissStorageKey(config);
    if (!storageKey) return;

    try {
      sessionStorage.setItem(storageKey, "1");
    } catch (error) {
      // Ignore storage errors.
    }
  }

  clearDiscreetUpsellDismissed(config) {
    const storageKey = this.getDiscreetUpsellDismissStorageKey(config);
    if (!storageKey) return;

    try {
      sessionStorage.removeItem(storageKey);
    } catch (error) {
      // Ignore storage errors.
    }
  }

  clearLegacyDiscreetUpsellDismissed() {
    if (this.didCleanLegacyDiscreetDismissal) return;
    this.didCleanLegacyDiscreetDismissal = true;

    try {
      sessionStorage.removeItem("dnp_upsell_dismissed");
    } catch (error) {
      // Ignore storage errors.
    }
  }

  hideDiscreetUpsell(upsell) {
    if (upsell) upsell.hidden = true;
  }

  hideTreatUpsell(upsell) {
    if (upsell) upsell.hidden = true;
  }

  hideAllTreatUpsells(upsells) {
    (upsells || []).forEach((upsell) => {
      this.hideTreatUpsell(upsell);
    });
  }

  ensureTreatUpsellTimerStarted(upsell, config) {
    if (!upsell || config.surface !== "cart" || !config.offerKey) return;
    if (!Number.isFinite(config.urgencyDurationSeconds) || config.urgencyDurationSeconds <= 0) {
      return;
    }

    const existingState = this.treatUpsellTimers.get(config.offerKey);
    if (existingState?.expired) {
      return;
    }

    if (!existingState) {
      this.treatUpsellTimers.set(config.offerKey, {
        durationMs: config.urgencyDurationSeconds * 1000,
        expiresAt: Date.now() + config.urgencyDurationSeconds * 1000,
        expired: false,
      });
    }

    this.renderTreatUpsellUrgency(upsell, config);
    this.ensureTreatUpsellTimerInterval();
  }

  ensureTreatUpsellTimerInterval() {
    if (this.treatUpsellTimerInterval) return;

    this.treatUpsellTimerInterval = window.setInterval(() => {
      this.tickTreatUpsellTimers();
    }, 1000);
  }

  tickTreatUpsellTimers() {
    if (!this.treatUpsellTimers.size) {
      this.clearTreatUpsellTimerInterval();
      return;
    }

    const now = Date.now();
    let hasActiveTimer = false;
    let didExpire = false;

    this.treatUpsellTimers.forEach((timerState) => {
      if (!timerState || timerState.expired) return;

      if (timerState.expiresAt <= now) {
        timerState.expired = true;
        didExpire = true;
        return;
      }

      hasActiveTimer = true;
    });

    this.getCartTreatUpsells().forEach((upsell) => {
      const config = this.getTreatUpsellConfig(upsell);
      this.renderTreatUpsellUrgency(upsell, config);
    });

    if (didExpire) {
      this.evaluateTreatUpsellEligibility();
    }

    if (!hasActiveTimer) {
      this.clearTreatUpsellTimerInterval();
    }
  }

  clearTreatUpsellTimerInterval() {
    if (!this.treatUpsellTimerInterval) return;

    window.clearInterval(this.treatUpsellTimerInterval);
    this.treatUpsellTimerInterval = null;
  }

  isTreatUpsellTimerExpired(config) {
    if (!config?.offerKey) return false;

    const timerState = this.treatUpsellTimers.get(config.offerKey);
    return Boolean(timerState?.expired);
  }

  renderTreatUpsellUrgency(upsell, config) {
    const urgencyElement = upsell?.querySelector("[data-ot-upsell-urgency]");
    if (!urgencyElement) return;

    const timerState = config?.offerKey ? this.treatUpsellTimers.get(config.offerKey) : null;
    const timerElement = urgencyElement.querySelector("[data-ot-upsell-timer]");
    const progressElement = urgencyElement.querySelector("[data-ot-upsell-progress]");

    if (!timerState || timerState.expired) {
      if (timerElement && Number.isFinite(config?.urgencyDurationSeconds)) {
        timerElement.textContent = this.formatTreatUpsellTimer(
          timerState?.expired ? 0 : Math.max(Number(config.urgencyDurationSeconds), 0)
        );
      }
      if (progressElement) {
        progressElement.style.width = timerState?.expired ? "0%" : "100%";
      }
      return;
    }

    const remainingMs = Math.max(timerState.expiresAt - Date.now(), 0);
    const remainingSeconds = Math.ceil(remainingMs / 1000);
    const progressPercent =
      timerState.durationMs > 0
        ? Math.max(Math.min((remainingMs / timerState.durationMs) * 100, 100), 0)
        : 0;

    if (timerElement) {
      timerElement.textContent = this.formatTreatUpsellTimer(remainingSeconds);
    }

    if (progressElement) {
      progressElement.style.width = `${progressPercent}%`;
    }
  }

  setDiscreetUpsellLoading(upsell, isLoading) {
    const acceptBtn = upsell?.querySelector("[data-dnp-upsell-accept]");
    const dismissBtn = upsell?.querySelector("[data-dnp-upsell-dismiss]");

    if (acceptBtn) {
      acceptBtn.classList.toggle("loading", isLoading);
      acceptBtn.disabled = isLoading;
      acceptBtn.setAttribute("aria-disabled", isLoading ? "true" : "false");
    }

    if (dismissBtn) {
      dismissBtn.disabled = isLoading;
      dismissBtn.setAttribute("aria-disabled", isLoading ? "true" : "false");
    }
  }

  setTreatUpsellLoading(upsell, isLoading) {
    const acceptBtn = upsell?.querySelector("[data-ot-upsell-accept]");
    const dismissBtn = upsell?.querySelector("[data-ot-upsell-dismiss]");

    if (acceptBtn) {
      acceptBtn.classList.toggle("loading", isLoading);
      acceptBtn.disabled = isLoading;
      acceptBtn.setAttribute("aria-disabled", isLoading ? "true" : "false");
    }

    if (dismissBtn) {
      dismissBtn.disabled = isLoading;
      dismissBtn.setAttribute("aria-disabled", isLoading ? "true" : "false");
    }
  }

  setDiscreetUpsellStatus(upsell, message) {
    const statusElement = upsell?.querySelector("[data-dnp-upsell-status]");
    if (statusElement) statusElement.textContent = message || "";
  }

  setTreatUpsellStatus(upsell, message) {
    const statusElement = upsell?.querySelector("[data-ot-upsell-status]");
    if (statusElement) statusElement.textContent = message || "";
  }

  setDiscreetUpsellError(upsell, message) {
    const errorElement = upsell?.querySelector("[data-dnp-upsell-error]");
    if (!errorElement) return;

    if (message) {
      errorElement.textContent = message;
      errorElement.hidden = false;
    } else {
      errorElement.hidden = true;
    }
  }

  setTreatUpsellError(upsell, message) {
    const errorElement = upsell?.querySelector("[data-ot-upsell-error]");
    if (!errorElement) return;

    if (message) {
      errorElement.textContent = message;
      errorElement.hidden = false;
    } else {
      errorElement.hidden = true;
    }
  }

  dismissTreatUpsell(upsell) {
    const config = this.getTreatUpsellConfig(upsell);
    if (config.surface === "pdp") {
      if (config.offerKey) this.dismissedPdpTreatUpsellKeys.add(config.offerKey);
      this.hideTreatUpsell(upsell);
      return;
    }

    if (config.offerKey) {
      this.dismissedTreatUpsellKeysForCurrentOpen.add(config.offerKey);
    }
    this.hideTreatUpsell(upsell);
  }

  async addTreatUpsellItem(upsell) {
    if (window.GoodLifeCart && typeof window.GoodLifeCart.enqueue === "function") {
      return window.GoodLifeCart.enqueue(() => this.addTreatUpsellItemNow(upsell));
    }

    return this.addTreatUpsellItemNow(upsell);
  }

  async addTreatUpsellItemNow(upsell) {
    if (this.isTreatUpsellAddInProgress) return;

    const config = this.getTreatUpsellConfig(upsell);
    if (!Number.isFinite(config.targetProductId) || !Number.isFinite(config.targetVariantId)) {
      return;
    }
    const targetLabel = config.targetLabel || "treats";
    const targetLabelLower = targetLabel.toLowerCase();

    this.isTreatUpsellAddInProgress = true;
    this.setTreatUpsellLoading(upsell, true);
    this.setTreatUpsellError(upsell, "");
    this.setTreatUpsellStatus(upsell, "Updating your cart...");

    let sourceAdded = false;

    try {
      const cart = await this.fetchCartJson();
      const cartState = this.getCartTreatUpsellState(cart, config);
      const selectedVariantId =
        this.getSelectedTreatVariantId(upsell) || config.targetVariantId;

      if (config.surface === "cart" && !cartState.hasSource) {
        this.hideTreatUpsell(upsell);
        this.setTreatUpsellStatus(upsell, "");
        return;
      }

      if (cartState.hasTarget || cartState.hasHiddenHandle) {
        this.hideTreatUpsell(upsell);
        this.setTreatUpsellStatus(upsell, "");
        return;
      }

      if (!Number.isFinite(Number(selectedVariantId))) {
        throw new Error("Please choose a flavor.");
      }

      // PDP surface: if the qualifying source product isn't in the cart yet,
      // add the shopper's currently selected source variant/qty first so both
      // products land in the cart together.
      if (config.surface === "pdp" && !cartState.hasSource) {
        const sourceVariantId = this.getPdpSelectedSourceVariantId(config);
        if (!Number.isFinite(sourceVariantId) || sourceVariantId <= 0) {
          throw new Error("Please choose a product option above.");
        }
        const sourceQty = this.getPdpSelectedSourceQuantity();
        const sourceAddResponse = await this.withThrottleRetry(() =>
          this.cartAddItem(sourceVariantId, sourceQty, {})
        );
        sourceAdded = true;
        if (sourceAddResponse?.price) {
          this.lastAddedItemPrice = Number(sourceAddResponse.price);
        }
        if (sourceAddResponse?.sku) {
          this.lastAddedItemSku = sourceAddResponse.sku;
        }
      }

      const addResponse = await this.withThrottleRetry(() =>
        this.cartAddItem(selectedVariantId, 1, {
          _upsell_skip: "true",
        })
      );

      if (addResponse?.price) {
        this.lastAddedItemPrice = Number(addResponse.price);
      }

      if (addResponse?.sku) {
        this.lastAddedItemSku = addResponse.sku;
      }

      this.hideTreatUpsell(upsell);
      this.setTreatUpsellStatus(upsell, `${targetLabel} added. Refreshing cart...`);

      await this.refreshCartDrawerSections();
      const refreshedCart = await this.fetchCartJson();
      await this.evaluatePdpTreatUpsellEligibility(refreshedCart);
      this.setTreatUpsellStatus(upsell, "");
    } catch (error) {
      console.error("[Treat Upsell] Add failed", error);
      this.setTreatUpsellError(
        upsell,
        error?.message || `We couldn't add the ${targetLabelLower} right now. Please try again.`
      );
      this.setTreatUpsellStatus(upsell, "");

      // If the source product was already added before the failure, refresh the
      // drawer so the user can see and act on it rather than leaving it silently
      // orphaned in the cart.
      if (sourceAdded) {
        try {
          await this.refreshCartDrawerSections();
          const refreshedCart = await this.fetchCartJson();
          await this.evaluatePdpTreatUpsellEligibility(refreshedCart);
        } catch (refreshError) {
          console.error(
            "[Treat Upsell] Drawer refresh after partial failure failed",
            refreshError
          );
        }
      }
    } finally {
      this.setTreatUpsellLoading(upsell, false);
      this.isTreatUpsellAddInProgress = false;
    }
  }

  async swapDogSilencerToDiscreetPack(upsell) {
    if (window.GoodLifeCart && typeof window.GoodLifeCart.enqueue === "function") {
      return window.GoodLifeCart.enqueue(() =>
        this.swapDogSilencerToDiscreetPackNow(upsell)
      );
    }

    return this.swapDogSilencerToDiscreetPackNow(upsell);
  }

  async swapDogSilencerToDiscreetPackNow(upsell) {
    if (this.isDiscreetSwapInProgress) return;

    const config = this.getDiscreetUpsellConfig(upsell);
    if (!config.sourceHandle || !Number.isFinite(config.targetProductId)) return;

    this.isDiscreetSwapInProgress = true;
    this.setDiscreetUpsellLoading(upsell, true);
    this.setDiscreetUpsellError(upsell, "");
    this.showCartDrawerError("");
    this.setDiscreetUpsellStatus(upsell, "Updating your cart...");

    try {
      const cart = await this.fetchCartJson();
      const plan = this.buildDiscreetSwapPlan(cart, config);

      if (!plan.sourceLines.length) {
        this.setDiscreetUpsellDismissed(config);
        this.hideDiscreetUpsell(upsell);
        this.setDiscreetUpsellStatus(upsell, "");
        return;
      }

      if (plan.hasTargetLine) {
        this.setDiscreetUpsellDismissed(config);
        this.hideDiscreetUpsell(upsell);
        this.setDiscreetUpsellStatus(upsell, "");
        return;
      }

      const tasks = [];
      let latestAddResponse = null;

      plan.warrantyLines.forEach((line) => {
        tasks.push(() => this.cartChangeByKey(line.key, 0));
      });

      plan.sourceLines.forEach((line) => {
        const targetVariantId = this.getMappedVariantIdForLine(line, config);
        if (!Number.isFinite(targetVariantId)) return;

        const safeProperties = this.getSafeSwapProperties(line.properties);

        tasks.push(async () => {
          latestAddResponse = await this.cartAddItem(
            targetVariantId,
            line.quantity,
            safeProperties
          );
        });
        tasks.push(() => this.cartChangeByKey(line.key, 0));
      });

      if (!tasks.length) {
        this.setDiscreetUpsellDismissed(config);
        this.hideDiscreetUpsell(upsell);
        this.setDiscreetUpsellStatus(upsell, "");
        return;
      }

      await this.runCartMutationQueue(tasks);

      let refreshedCart = await this.fetchCartJson();
      const targetLine = this.findDiscreetNeighborLine(refreshedCart, config);

      let discountApplyFailed = false;
      if (config.discountCode) {
        try {
          refreshedCart = await this.applyDiscreetUpsellDiscount(refreshedCart, config);
        } catch (discountError) {
          discountApplyFailed = true;
          console.error("[DNP Upsell] Discount apply failed", discountError);
        }
      }

      if (latestAddResponse?.price) {
        this.lastAddedItemPrice = Number(latestAddResponse.price);
      } else if (targetLine) {
        const linePrice = Number(targetLine.final_price || targetLine.price);
        if (Number.isFinite(linePrice) && linePrice > 0) {
          this.lastAddedItemPrice = linePrice;
        }
      }

      if (latestAddResponse?.sku) {
        this.lastAddedItemSku = latestAddResponse.sku;
      } else if (targetLine?.sku) {
        this.lastAddedItemSku = targetLine.sku;
      }

      this.skipWarranty = false;
      this.hideDiscreetUpsell(upsell);
      this.setDiscreetUpsellStatus(upsell, "Upgrade added. Refreshing cart...");

      await this.refreshCartDrawerSections();
      if (discountApplyFailed) {
        this.showCartDrawerError(
          `Upgrade added, but free shipping could not be applied automatically. Please enter ${config.discountCode} at checkout.`
        );
      } else {
        this.showCartDrawerError("");
        // renderContents schedules the drawer reopen after replacing markup;
        // clear any stale cart-level error again after that async render cycle.
        window.setTimeout(() => this.showCartDrawerError(""), 0);
        window.setTimeout(() => this.showCartDrawerError(""), 300);
      }
      this.setDiscreetUpsellStatus(upsell, "");
    } catch (error) {
      console.error("[DNP Upsell] Swap failed", error);
      this.setDiscreetUpsellError(
        upsell,
        "We couldn't update your cart. Please try again."
      );
      this.setDiscreetUpsellStatus(upsell, "");
    } finally {
      this.setDiscreetUpsellLoading(upsell, false);
      this.isDiscreetSwapInProgress = false;
    }
  }

  buildDiscreetSwapPlan(cart, config) {
    const items = Array.isArray(cart?.items) ? cart.items : [];

    const sourceLines = items.filter(
      (item) =>
        this.isSourceDogSilencerLine(item, config.sourceHandle) &&
        Number(item.quantity) > 0
    );

    const hasTargetLine = items.some((item) =>
      this.isDiscreetNeighborPackLine(item, config.targetProductId)
    );

    const sourceSkus = new Set(
      sourceLines
        .map((line) => String(line.sku || "").trim())
        .filter((sku) => sku.length > 0)
    );

    const warrantyLines = items.filter((item) => {
      if (!this.isWarrantyLine(item)) return false;
      const parentSku = String(item?.properties?.parent_sku || "").trim();
      return parentSku && sourceSkus.has(parentSku);
    });

    return {
      sourceLines,
      hasTargetLine,
      warrantyLines,
    };
  }

  isWarrantyLine(item) {
    if (!item || typeof item !== "object") return false;
    const productType = String(item?.properties?.product_type || "").toLowerCase();
    return productType === "warranty";
  }

  isSourceDogSilencerLine(item, sourceHandle) {
    if (!item || typeof item !== "object") return false;
    if (this.isWarrantyLine(item)) return false;

    // Check if the item explicitly requests to skip the upsell
    if (item.properties && item.properties._upsell_skip === 'true') return false;

    const normalizedSource = String(sourceHandle || "").trim().toLowerCase();
    if (!normalizedSource) return false;

    const handleCandidates = [item.handle, item.product_handle]
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean);

    if (handleCandidates.includes(normalizedSource)) return true;

    const productUrl = String(item.url || "").toLowerCase();
    return productUrl.includes(`/products/${normalizedSource}`);
  }

  isDiscreetNeighborPackLine(item, targetProductId) {
    if (!item || typeof item !== "object") return false;
    if (this.isWarrantyLine(item)) return false;

    return Number(item.product_id) === Number(targetProductId);
  }

  isTreatUpsellSourceLine(item, sourceHandles) {
    if (!item || typeof item !== "object") return false;
    if (this.isWarrantyLine(item)) return false;

    if (item.properties && item.properties._upsell_skip === "true") return false;
    const normalizedSources = Array.isArray(sourceHandles)
      ? sourceHandles
          .map((handle) => String(handle || "").trim().toLowerCase())
          .filter(Boolean)
      : [String(sourceHandles || "").trim().toLowerCase()].filter(Boolean);
    if (!normalizedSources.length) return false;

    const handleCandidates = [item.handle, item.product_handle]
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean);

    if (handleCandidates.some((candidate) => normalizedSources.includes(candidate))) {
      return true;
    }

    const productUrl = String(item.url || "").toLowerCase();
    return normalizedSources.some((sourceHandle) =>
      productUrl.includes(`/products/${sourceHandle}`)
    );
  }

  isTreatUpsellTargetLine(item, targetProductId) {
    if (!item || typeof item !== "object") return false;
    if (this.isWarrantyLine(item)) return false;

    return Number(item.product_id) === Number(targetProductId);
  }

  getCartTreatUpsellState(cart, config) {
    const items = Array.isArray(cart?.items) ? cart.items : [];

    let hasSource = false;
    let hasTarget = false;
    let hasHiddenHandle = false;

    items.forEach((item) => {
      if (!hasSource && this.isTreatUpsellSourceLine(item, config.sourceHandles)) {
        hasSource = Number(item.quantity) > 0;
      }

      if (!hasTarget && this.isTreatUpsellTargetLine(item, config.targetProductId)) {
        hasTarget = Number(item.quantity) > 0;
      }

      if (
        !hasHiddenHandle &&
        this.isTreatUpsellSourceLine(item, config.hiddenIfHandles)
      ) {
        hasHiddenHandle = Number(item.quantity) > 0;
      }
    });

    return {
      hasSource,
      hasTarget,
      hasHiddenHandle,
    };
  }

  getMappedVariantIdForLine(line, config) {
    const selectedVariantId = Number(config?.selectedVariantId);
    if (Number.isFinite(selectedVariantId) && selectedVariantId > 0) {
      return selectedVariantId;
    }

    const sourceVariantId = Number(line?.id || line?.variant_id);
    if (Number.isFinite(sourceVariantId) && config?.sourceVariantMap) {
      const directMappedVariantId = Number(config.sourceVariantMap[sourceVariantId]);
      if (Number.isFinite(directMappedVariantId) && directMappedVariantId > 0) {
        return directMappedVariantId;
      }
    }

    const faceplate = this.getFaceplateValue(line);
    const normalizedFaceplate = this.normalizeFaceplate(faceplate);

    const fallbackVariant = config.variantMap["matte white"];
    const mappedVariant = config.variantMap[normalizedFaceplate];

    return Number(mappedVariant || fallbackVariant);
  }

  getFaceplateValue(line) {
    const optionsWithValues = line?.options_with_values;

    if (Array.isArray(optionsWithValues)) {
      const option = optionsWithValues.find((candidate) =>
        String(candidate?.name || "")
          .toLowerCase()
          .includes("faceplate")
      );

      if (option?.value) return String(option.value);
    }

    if (optionsWithValues && typeof optionsWithValues === "object") {
      for (const [name, value] of Object.entries(optionsWithValues)) {
        if (String(name || "").toLowerCase().includes("faceplate") && value) {
          return String(value);
        }
      }
    }

    const variantTitle = String(line?.variant_title || "").toLowerCase();
    if (variantTitle.includes("no faceplate")) return "No Faceplate";
    if (variantTitle.includes("matte white")) return "Matte White";
    if (variantTitle.includes("camouflage")) return "Camouflage Pattern";
    if (variantTitle.includes("tree bark")) return "Tree Bark";

    return "Matte White";
  }

  normalizeFaceplate(faceplate) {
    const normalized = String(faceplate || "")
      .toLowerCase()
      .trim()
      .replace(/\\s+/g, " ");

    if (!normalized) return "matte white";
    if (normalized.includes("no faceplate")) return "no faceplate";
    if (normalized.includes("camo")) return "camouflage pattern";
    if (normalized.includes("camouflage")) return "camouflage pattern";
    if (normalized.includes("tree bark") || normalized.includes("bark")) {
      return "tree bark";
    }
    if (normalized.includes("white")) return "matte white";

    return normalized;
  }

  getSafeSwapProperties(properties) {
    if (!properties || typeof properties !== "object") return undefined;

    const safeProperties = {};

    Object.entries(properties).forEach(([key, value]) => {
      if (value == null || value === "") return;
      if (String(key).startsWith("_")) return;
      if (key === "product_type") return;
      if (key === "parent_sku") return;
      if (key === "percentage") return;
      safeProperties[key] = value;
    });

    return Object.keys(safeProperties).length ? safeProperties : undefined;
  }

  findDiscreetNeighborLine(cart, config) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    return (
      items.find((item) =>
        this.isDiscreetNeighborPackLine(item, config.targetProductId)
      ) || null
    );
  }

  async applyDiscreetUpsellDiscount(cart, config) {
    if (!config?.discountCode) return cart;

    const discountHelper = window.GoodLifeOpenBoxSolarPackDiscount;
    if (
      config.targetHandle === discountHelper?.bundleHandle &&
      typeof discountHelper.apply === "function"
    ) {
      return discountHelper.apply(cart);
    }

    const existingCodes = (cart?.discount_codes || [])
      .map((discount) => discount.code)
      .filter(Boolean);
    const discountCodes = Array.from(
      new Set([...existingCodes, config.discountCode])
    );
    const requestConfig = fetchConfig("javascript");
    requestConfig.headers["X-Requested-With"] = "XMLHttpRequest";
    requestConfig.body = JSON.stringify({
      discount: discountCodes.join(","),
    });

    const response = await fetch(`${routes.cart_update_url}`, requestConfig);
    const updatedCart = await response.json();
    const appliedDiscount = updatedCart?.discount_codes?.find(
      (discount) => discount.code === config.discountCode
    );

    if (!response.ok || updatedCart.status || !appliedDiscount || appliedDiscount.applicable === false) {
      throw new Error("Cart discount code could not be applied.");
    }

    return updatedCart;
  }

  showCartDrawerError(message) {
    const errorElement = document.getElementById("CartDrawer-CartErrors");
    if (errorElement) errorElement.textContent = message || "";
  }

  async runCartMutationQueue(tasks) {
    for (const task of tasks) {
      await this.withThrottleRetry(task);
      await this.delay(140);
    }
  }

  async withThrottleRetry(task) {
    let attempts = 0;

    while (attempts < 2) {
      try {
        return await task();
      } catch (error) {
        attempts += 1;

        if (!this.isRateLimitError(error) || attempts >= 2) {
          throw error;
        }

        await this.delay(attempts === 1 ? 450 : 900);
      }
    }

    return null;
  }

  isRateLimitError(error) {
    if (Number(error?.status) === 429) return true;

    const message = String(error?.message || "").toLowerCase();
    return message.includes("throttled") || message.includes("rate limit");
  }

  async fetchCartJson() {
    const response = await fetch(`${routes.cart_url}.js`, {
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    if (!response.ok) {
      const error = new Error("Failed to load cart.");
      error.status = response.status;
      throw error;
    }

    return response.json();
  }

  async cartAddItem(variantId, quantity, properties) {
    const payload = {
      id: Number(variantId),
      quantity: Number(quantity) || 1,
    };

    if (properties) payload.properties = properties;

    return this.cartMutationRequest(`${routes.cart_add_url}.js`, payload);
  }

  async cartChangeByKey(key, quantity) {
    return this.cartMutationRequest(`${routes.cart_change_url}.js`, {
      id: key,
      quantity,
    });
  }

  async cartMutationRequest(url, payload) {
    const config = fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    config.body = JSON.stringify(payload);

    const response = await fetch(url, config);

    let data;
    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    if (!response.ok || (data && data.status)) {
      const message =
        (data && (data.description || data.message)) ||
        "Cart update request failed.";
      const error = new Error(message);
      error.status = Number(data?.status || response.status);
      error.payload = data;
      throw error;
    }

    return data;
  }

  async refreshCartDrawerSections() {
    const sections = this.getSectionsToRender();
    const sectionResponses = {};

    await Promise.all(
      sections.map(async (section) => {
        sectionResponses[section.id] = await this.fetchSectionHtml(section.id);
      })
    );

    this.renderContents({
      sections: sectionResponses,
      price: this.lastAddedItemPrice,
      sku: this.lastAddedItemSku,
    });
  }

  async fetchSectionHtml(sectionId) {
    const connector = window.location.search ? "&" : "?";
    const url = `${window.location.pathname}${window.location.search}${connector}section_id=${encodeURIComponent(
      sectionId
    )}&_=${Date.now()}`;

    const response = await fetch(url, {
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    if (!response.ok) {
      const error = new Error(`Failed to load section: ${sectionId}`);
      error.status = response.status;
      throw error;
    }

    return response.text();
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  formatMoney(cents) {
    if (typeof Shopify !== "undefined" && typeof Shopify.formatMoney === "function") {
      return Shopify.formatMoney(cents, "${{amount}}");
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format((Number(cents) || 0) / 100);
  }

  formatSavingsLabel(cents) {
    const amount = Number(cents);
    if (!Number.isFinite(amount) || amount <= 0) return "";

    const dollars = amount / 100;
    if (Number.isInteger(dollars)) {
      return `Save $${dollars}`;
    }

    return `Save ${this.formatMoney(amount)}`;
  }

  formatTreatUpsellTimer(totalSeconds) {
    const safeSeconds = Math.max(Number(totalSeconds) || 0, 0);
    const minutes = Math.floor(safeSeconds / 60);
    const seconds = safeSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  initWarrantyListeners() {
    const warrantySection = this.querySelector("warranty-section");
    if (!warrantySection) return;

    const addBtn = warrantySection.querySelector("[data-addprotection]");
    const noBtn = warrantySection.querySelector("[data-nothanks]");
    const form = warrantySection.querySelector("#warranty-form");

    if (addBtn) {
      addBtn.onclick = (e) => {
        e.preventDefault();
        const formData = new FormData(form);
        const variantId = formData.get("warranty-variant");
        if (variantId) {
          addBtn.classList.add("loading");
          this.addWarrantyItem(variantId, addBtn);
        }
      };
    }

    if (noBtn) {
      noBtn.onclick = (e) => {
        e.preventDefault();
        warrantySection.setAttribute("hidden", "");
        const cartItems = this.querySelector("cart-drawer-items");
        const footer = this.querySelector(".drawer__footer-footer");
        const header = this.querySelector(".drawer__footer-content");

        if (cartItems) cartItems.removeAttribute("hidden");
        if (footer) footer.removeAttribute("hidden");
        if (header) header.removeAttribute("hidden");
        this.initDiscreetNeighborUpsell();
        this.initTreatUpsells();
      };
    }
  }

  updateWarrantyPrices() {
    if (!this.lastAddedItemPrice) return;

    const warrantyOptions = this.querySelectorAll(
      ".warranty-option-card input[data-percentage]"
    );
    warrantyOptions.forEach((input) => {
      const percentage = parseFloat(input.dataset.percentage);
      if (!isNaN(percentage) && percentage > 0) {
        const priceElement =
          input.parentElement.querySelector(".warranty-price");
        if (priceElement) {
          const newPriceCents = (this.lastAddedItemPrice * percentage) / 100;
          // Format money - simple version for now, assuming USD/standard formatting
          // Assuming lastAddedItemPrice is in cents
          const newPrice = newPriceCents / 100;

          // Try to use Shopify.formatMoney if available, otherwise Intl
          let formattedPrice;
          if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
            formattedPrice = Shopify.formatMoney(newPriceCents, "${{amount}}");
          } else {
            formattedPrice = new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(newPrice);
          }

          priceElement.textContent = formattedPrice;
        }
      }
    });
  }

  addWarrantyItem(variantId, btn) {
    if (window.GoodLifeCart && typeof window.GoodLifeCart.enqueue === "function") {
      return window.GoodLifeCart.enqueue(() => this.addWarrantyItemNow(variantId, btn));
    }

    return this.addWarrantyItemNow(variantId, btn);
  }

  addWarrantyItemNow(variantId, btn) {
    const config = fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    delete config.headers["Content-Type"];

    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", 1);

    if (this.lastAddedItemSku) {
      formData.append("properties[parent_sku]", this.lastAddedItemSku);
    }

    // Find percentage
    const selectedInput = this.querySelector(
      `input[name="warranty-variant"][value="${variantId}"]`
    );
    if (selectedInput && selectedInput.dataset.percentage) {
      formData.append(
        "properties[percentage]",
        selectedInput.dataset.percentage
      );
    }

    formData.append("properties[product_type]", "warranty");

    formData.append(
      "sections",
      this.getSectionsToRender().map((section) => section.id)
    );
    formData.append("sections_url", window.location.pathname);

    config.body = formData;

    return fetch(routes.cart_add_url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          console.error(response);
          return;
        }
        this.skipWarranty = true;
        this.renderContents(response);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        btn.classList.remove("loading");
      });
  }

  getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-icon-bubble--mobile",
        section: "cart-icon-bubble--mobile",
        selector: ".shopify-section",
      },
    ];
  }

  async updateQuantity(line, quantity, name, lineIndex = line, lineKey = null) {
    await super.updateQuantity(line, quantity, name, lineIndex, lineKey);
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer && typeof cartDrawer.refreshCartUpsells === "function") {
      cartDrawer.refreshCartUpsells();
    }
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
